import { useMemo } from 'react';

import { ApiOutlined } from '@ant-design/icons';
import { Alert, App, Col, Form, Row, Segmented, Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import IntegrationCard from 'components/IntegrationCard/IntegrationCard';

import {
  TiendaPerfectaDistributor,
  TiendaPerfectaDistributorTier,
} from 'models/TiendaPerfecta';

import { FunctionsHelper } from 'utils/FunctionsHelper';

export interface TiendaPerfectaAccessValue {
  tier: TiendaPerfectaDistributorTier;
  distributorIds: string[];
}

export const EMPTY_TIENDA_PERFECTA_ACCESS: TiendaPerfectaAccessValue = {
  tier: 'low',
  distributorIds: [],
};

/**
 * Traduce el estado del bloque a los dos campos del body de rol. La compañía no
 * viaja: el rol la hereda de la compañía de Touch Portal a la que pertenece.
 */
export const buildTiendaPerfectaPayload = (
  value: TiendaPerfectaAccessValue,
  integrationEnabled: boolean
) => {
  if (!integrationEnabled) {
    return {
      tiendaPerfectaDistributorTier: null,
      tiendaPerfectaDistributorIds: null,
    };
  }

  return {
    tiendaPerfectaDistributorTier: value.tier,
    // El tier `top` hereda todos los distribuidores: la lista se guarda vacía.
    tiendaPerfectaDistributorIds:
      value.tier === 'top' ? [] : value.distributorIds,
  };
};

interface TiendaPerfectaAccessFieldsProps {
  value: TiendaPerfectaAccessValue;
  onChange: (value: TiendaPerfectaAccessValue) => void;
  /** Catálogo de la compañía de Tienda Perfecta ligada a la compañía activa. */
  distributors: TiendaPerfectaDistributor[];
  distributorsLoading?: boolean;
  disabled?: boolean;
  /**
   * Distribuidores ya hidratados por el rol. Se usan para que una selección
   * previa siga siendo legible aunque el distribuidor esté dado de baja.
   */
  knownDistributors?: TiendaPerfectaDistributor[];
}

const I18N_BASE =
  'scenes.settings.roles.components.tienda_perfecta_access_fields';

function TiendaPerfectaAccessFields({
  value,
  onChange,
  distributors,
  distributorsLoading = false,
  disabled = false,
  knownDistributors = [],
}: TiendaPerfectaAccessFieldsProps) {
  const { message } = App.useApp();
  const { t } = useTranslation();

  const { tier, distributorIds } = value;

  // Catálogo + los conocidos del rol, para no perder una selección previa
  // mientras el catálogo todavía no llegó.
  const distributorPool = useMemo(() => {
    const pool = new Map<string, TiendaPerfectaDistributor>();
    knownDistributors.forEach((d) => pool.set(d.id, d));
    distributors.forEach((d) => pool.set(d.id, d));
    return pool;
  }, [distributors, knownDistributors]);

  const selectedIds = useMemo(() => new Set(distributorIds), [distributorIds]);

  // Un distribuidor dado de baja solo se ofrece si ya estaba asignado: el
  // backend solo acepta activos al guardar.
  const distributorOptions = useMemo(
    () =>
      Array.from(distributorPool.values())
        .filter((d) => d.active || selectedIds.has(d.id))
        .map((d) => ({
          value: d.id,
          label: d.active
            ? d.tradeName
            : t(`${I18N_BASE}.labels.inactive_distributor`, {
                name: d.tradeName,
              }),
          title: `${d.tradeName} · ${d.code}`,
        })),
    [distributorPool, selectedIds, t]
  );

  const inactiveSelected = useMemo(
    () =>
      distributorIds
        .map((id) => distributorPool.get(id))
        .filter((d): d is TiendaPerfectaDistributor => !!d && !d.active),
    [distributorIds, distributorPool]
  );

  const handleTierChange = (nextTier: TiendaPerfectaDistributorTier) => {
    if (nextTier === 'top') {
      onChange({ tier: nextTier, distributorIds: [] });
      return;
    }

    if (nextTier === 'low' && distributorIds.length > 1) {
      message.warning(t(`${I18N_BASE}.messages.low_tier_truncated`), 3.5);
      onChange({ tier: nextTier, distributorIds: distributorIds.slice(0, 1) });
      return;
    }

    onChange({ ...value, tier: nextTier });
  };

  const filterOption = (search: string, option?: any) =>
    FunctionsHelper.normalizeText(option?.title ?? '').includes(
      FunctionsHelper.normalizeText(search)
    );

  const summary =
    tier === 'top' ? (
      <Tag color="green">{t(`${I18N_BASE}.summary.full_access`)}</Tag>
    ) : (
      <Tag color="blue">
        {t(`${I18N_BASE}.summary.distributors`, {
          tier: t(`${I18N_BASE}.tiers.${tier}`),
          count: distributorIds.length,
        })}
      </Tag>
    );

  return (
    <IntegrationCard
      title={t(`${I18N_BASE}.title`)}
      icon={<ApiOutlined />}
      summary={summary}
    >
      <Row gutter={24}>
        <Col xs={24}>
          <Form.Item
            label={t(`${I18N_BASE}.labels.tier`)}
            style={{ marginBottom: 16 }}
          >
            <Segmented<TiendaPerfectaDistributorTier>
              block
              value={tier}
              onChange={handleTierChange}
              disabled={disabled}
              options={[
                { value: 'top', label: t(`${I18N_BASE}.tiers.top`) },
                { value: 'medium', label: t(`${I18N_BASE}.tiers.medium`) },
                { value: 'low', label: t(`${I18N_BASE}.tiers.low`) },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {tier === 'top' && (
        <Alert
          type="info"
          showIcon
          message={t(`${I18N_BASE}.alerts.top_tier`)}
        />
      )}

      {tier !== 'top' && (
        <Row gutter={24}>
          <Col xs={24}>
            <Form.Item
              label={
                tier === 'low'
                  ? t(`${I18N_BASE}.labels.distributor_single`)
                  : t(`${I18N_BASE}.labels.distributors`)
              }
              style={{
                marginBottom: inactiveSelected.length > 0 ? 16 : 0,
              }}
            >
              <Select
                mode={tier === 'medium' ? 'multiple' : undefined}
                value={
                  tier === 'medium'
                    ? distributorIds
                    : distributorIds[0] ?? undefined
                }
                onChange={(next: string | string[]) =>
                  onChange({
                    ...value,
                    distributorIds:
                      next === undefined
                        ? []
                        : Array.isArray(next)
                        ? next
                        : [next],
                  })
                }
                options={distributorOptions}
                loading={distributorsLoading}
                showSearch
                allowClear
                disabled={disabled}
                filterOption={filterOption}
                placeholder={
                  tier === 'low'
                    ? t(`${I18N_BASE}.placeholders.distributor_single`)
                    : t(`${I18N_BASE}.placeholders.distributors`)
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {tier !== 'top' && inactiveSelected.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t(`${I18N_BASE}.alerts.inactive_selected`, {
            names: inactiveSelected.map((d) => d.tradeName).join(', '),
          })}
        />
      )}
    </IntegrationCard>
  );
}

export default TiendaPerfectaAccessFields;
